import './SuperAdminAssetsTable.scss';
import {map, get} from 'lodash';
import {format} from "date-fns";
import {DataGridWrapper} from "../../../../Utils/DataGridWrapper/DataGridWrapper";
import {SA_getCompanyFilesAPI, SA_getAllCompanies} from "../../../../Services/SA_api";
import {FileTypeCell} from "./FileTypeCell/FileTypeCell";
import {FileDownloadCell} from "./FileDownloadCell/FileDownloadCell";
import {ThumbnailCell} from "./ThumbnailCell/ThumbnailCell";
import {TagsCell} from "./TagsCell/TagsCell";
import {Button} from "../../../../Components/Form/Button/Button";
import {buttonColors} from "../../../../Components/Pickers/InputTheme";
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import React, {useContext, useEffect, useState, useRef} from "react";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import LoadingSpinner from "../../../../Components/LoadingSpinner/LoadingSpinner";
import {UploadPublicAssetModal} from "../UploadPublicAssetModal/UploadPublicAssetModal";

// Define columns for the data grid
const getColumns = async ({i18n}) => {
    return [
        {
            field: 'thumbnail',
            headerName: i18n.translate("superAdmin.assets.columns.thumbnail"),
            minWidth: 100,
            align: 'center',
            sortable: false,
            renderCell: (params) => <ThumbnailCell fileId={params.row.id} type={params.row.type} signedUrl={params.row.signedUrl} />
        },
        {
            field: 'presentationName',
            headerName: i18n.translate("superAdmin.assets.columns.fileName"),
            minWidth: 200,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'type',
            headerName: i18n.translate("superAdmin.assets.columns.type"),
            minWidth: 120,
            align: 'right',
            sortable: false,
            renderCell: (params) => <FileTypeCell type={params.value} />
        },
        {
            field: 'companyName',
            headerName: i18n.translate("superAdmin.assets.columns.company"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'createdBy',
            headerName: i18n.translate("superAdmin.assets.columns.createdBy"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'tags',
            headerName: i18n.translate("superAdmin.assets.columns.tags"),
            minWidth: 200,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                // Log the row data to see what's being passed to TagsCell
                console.log('SuperAdminAssetsTable - renderCell - params.row:', {
                    id: params.row.id,
                    companyId: params.row.company?.id,
                    company: params.row.company
                });

                return (
                    <TagsCell 
                        fileId={params.row.id} 
                        tags={params.row.tags} 
                        companyId={params.row.company?.id}
                        onTagsUpdated={(newTags) => {
                            // Update the row data with new tags
                            params.row.tags = newTags;
                            // Refresh the data grid to show updated tags
                            if (params.api.getRowNode(params.row.id)) {
                                params.api.updateRows([{ id: params.row.id, tags: newTags }]);
                            }
                        }}
                    />
                );
            }
        },
        {
            field: 'updatedAt',
            headerName: i18n.translate("superAdmin.assets.columns.lastModified"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{params.value}</div>,
        },
        {
            field: 'download',
            headerName: i18n.translate("superAdmin.assets.columns.download"),
            minWidth: 120,
            align: 'right',
            sortable: false,
            renderCell: (params) => <FileDownloadCell fileId={params.row.id} signedUrl={params.row.signedUrl} />
        }
    ];
};

// Convert API response data to the format expected by the data grid
const convertData = (companyFiles) => {
    return map(companyFiles, (file) => {
        // Log the file data to see what's coming from the API
        console.log('SuperAdminAssetsTable - convertData - file:', {
            id: file.id,
            companyId: file.company?.id,
            companyName: file.company?.companyName
        });

        return {
            id: file.id,
            presentationName: file.presentationName || file.fileName,
            type: file.type,
            companyName: file.company?.companyName || '',
            createdBy: file.createdByUser ? `${file.createdByUser.firstName} ${file.createdByUser.lastName}` : '',
            updatedAt: format(new Date(file.updatedAt), 'yyyy-MM-dd HH:mm'),
            fileName: file.fileName,
            bucket: file.bucket,
            signedUrl: file.signedUrl, // Include the pre-generated signed URL
            tags: file.tags || [], // Include tags array
            company: file.company // Include the entire company object
        };
    });
};

// Define pagination model
const paginationModel = {
    page: 0,
    pageSize: 10,
};

// Get filter model with companies
const getFilterModel = ({companies}) => {
    return {
        filterValues: {
            onlyImageAndVideo: true,
            type: '' // Default to 'all'
        },
        filterData: {
            companies
        },
        showFilters: {
            search: true,
            fileType: true,
            companies: true
        }
    }
};

// Define buttons function to use i18n from context
const getButtons = (i18n, onUploadClick) => [
    {
        text: i18n.translate("superAdmin.assets.filters.clear"),
        icon: <ClearIcon />,
        color: buttonColors.GREY,
        onClick: async ({filterModel}) => {
            // Reset filter values but keep onlyImageAndVideo
            filterModel.filterValues = {
                onlyImageAndVideo: true
            };
            return Promise.resolve();
        }
    },
    {
        text: i18n.translate("superAdmin.assets.upload.uploadButton"),
        icon: <CloudUploadIcon />,
        color: buttonColors.GOLD,
        onClick: () => {
            onUploadClick();
            return Promise.resolve();
        }
    }
];

export const SuperAdminAssetsTable = () => {
    const {i18n} = useContext(I18nReactContext);
    const [companies, setCompanies] = useState();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const dataGridRef = useRef(null);

    // Initialize companies for filter
    const init = async () => {
        const companiesData = await SA_getAllCompanies();

        // Create select box options with 'All' and 'General Bank' options
        const selectBoxCompanies = [
            {
                value: "",
                text: i18n.translate("superAdmin.assets.filters.all")
            },
            {
                value: "none",
                text: i18n.translate("superAdmin.assets.filters.generalBank")
            },
            ...map(companiesData, (company) => {
                return {
                    value: get(company, 'id'),
                    text: get(company, 'companyName')
                }
            })
        ];

        // Sort companies alphabetically
        selectBoxCompanies.sort((a, b) => {
            // Keep 'All' and 'General Bank' at the top
            if (a.value === "") return -1;
            if (b.value === "") return 1;
            if (a.value === "none") return -1;
            if (b.value === "none") return 1;
            return a.text.localeCompare(b.text);
        });

        setCompanies(selectBoxCompanies);
    };

    useEffect(() => {
        init();
    }, []);

    // API function to get data
    const apiToGetData = async ({paginationModel, filterModel}) => {
        return SA_getCompanyFilesAPI({paginationModel, filterModel});
    };

    // Handle upload button click
    const handleUploadClick = () => {
        setIsUploadModalOpen(true);
    };

    // Handle upload success
    const handleUploadSuccess = () => {
        // Refresh the data grid
        if (dataGridRef.current) {
            dataGridRef.current.refreshData();
        }
    };

    if (companies) {
        return (
            <>
                <DataGridWrapper
                    ref={dataGridRef}
                    filterModel={getFilterModel({companies})}
                    disableColumnFilter
                    getColumns={getColumns}
                    paginationModel={paginationModel}
                    apiToGetData={apiToGetData}
                    dataConverter={convertData}
                    buttons={getButtons(i18n, handleUploadClick)}
                />

                <UploadPublicAssetModal
                    isOpen={isUploadModalOpen}
                    onClose={() => setIsUploadModalOpen(false)}
                    onSuccess={handleUploadSuccess}
                />
            </>
        );
    } else {
        return <LoadingSpinner />;
    }
};
